// Tutorial AI Panel - Side panel for AI-assisted content creation
// Content is NEVER inserted directly — always previewed first
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Sparkles, X, Send, Loader2, Copy, Plus, RefreshCcw,
  Wand2, CheckCircle, Type, Code, FileText, Eraser, PenLine,
  ChevronDown, ChevronUp, Zap, MessageSquare,
} from 'lucide-react';
import { toast } from 'sonner';
import useTutorialStore from '@/stores/tutorialStore';
import { motion, AnimatePresence } from 'framer-motion';

const AI_ACTIONS = [
  { id: 'generate', label: 'Générer du contenu', icon: Sparkles, description: 'Créer un tutoriel complet' },
  { id: 'improve', label: 'Améliorer', icon: Wand2, description: 'Améliorer la qualité du texte' },
  { id: 'fix', label: 'Corriger', icon: CheckCircle, description: 'Corriger l\'orthographe et grammaire' },
  { id: 'simplify', label: 'Simplifier', icon: Type, description: 'Rendre plus accessible' },
  { id: 'formal', label: 'Formaliser', icon: PenLine, description: 'Ton plus professionnel' },
  { id: 'summarize', label: 'Résumer', icon: FileText, description: 'Points clés en résumé' },
  { id: 'code', label: 'Exemple de code', icon: Code, description: 'Générer un exemple de code' },
];

const STYLES = [
  { value: 'pedagogique', label: 'Pédagogique' },
  { value: 'technique', label: 'Technique' },
  { value: 'professionnel', label: 'Professionnel' },
];

const CODE_LANGUAGES = [
  'python', 'javascript', 'typescript', 'bash', 'dockerfile',
  'yaml', 'json', 'html', 'css', 'sql', 'go', 'rust', 'java', 'c', 'cpp',
];

export default function TutorialAIPanel({ userId, onInsertContent, selectedText }) {
  const { aiPanelOpen, setAIPanelOpen, aiLoading, aiResult, aiHistory, aiGenerate, aiImprove, aiCodeExample, clearAIResult } = useTutorialStore();
  const [prompt, setPrompt] = useState('');
  const [activeAction, setActiveAction] = useState('generate');
  const [style, setStyle] = useState('pedagogique');
  const [codeLanguage, setCodeLanguage] = useState('python');
  const [showHistory, setShowHistory] = useState(false);
  const promptRef = useRef(null);
  const resultRef = useRef(null);

  // Auto-focus prompt when panel opens
  useEffect(() => {
    if (aiPanelOpen && promptRef.current) {
      setTimeout(() => promptRef.current?.focus(), 200);
    }
  }, [aiPanelOpen]);

  // Scroll to result when available
  useEffect(() => {
    if (aiResult && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [aiResult]);

  // Pre-fill with selected text for improvement actions
  useEffect(() => {
    if (selectedText && ['improve', 'fix', 'simplify', 'formal', 'summarize'].includes(activeAction)) {
      setPrompt(selectedText.slice(0, 2000));
    }
  }, [selectedText, activeAction]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!prompt.trim() && activeAction !== 'generate') return;

    let result = null;
    if (activeAction === 'generate') {
      if (!prompt.trim()) {
        toast.error('Décrivez le contenu à générer');
        return;
      }
      result = await aiGenerate({ prompt, style, language: 'fr' }, userId);
    } else if (activeAction === 'code') {
      if (!prompt.trim()) {
        toast.error('Décrivez l\'exemple de code souhaité');
        return;
      }
      result = await aiCodeExample({ topic: prompt, language: codeLanguage }, userId);
    } else {
      const text = prompt || selectedText;
      if (!text?.trim()) {
        toast.error('Sélectionnez du texte ou saisissez le texte à traiter');
        return;
      }
      result = await aiImprove({ text, action: activeAction }, userId);
    }
  };

  const handleInsert = () => {
    if (!aiResult) return;
    if (activeAction === 'generate' || activeAction === 'code') {
      // Insert TipTap JSON content
      onInsertContent?.(aiResult.content, 'json');
    } else {
      // Insert plain text replacement
      onInsertContent?.(aiResult.raw_text || aiResult.content, 'text');
    }
    clearAIResult();
    toast.success('Contenu inséré dans l\'éditeur');
  };

  const handleCopy = () => {
    const text = aiResult?.raw_text || aiResult?.content;
    if (text) {
      navigator.clipboard.writeText(typeof text === 'string' ? text : JSON.stringify(text));
      toast.success('Copié dans le presse-papiers');
    }
  };

  const handleRegenerate = () => {
    handleSubmit();
  };

  if (!aiPanelOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: '100%', opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="tut-ai-panel"
        data-testid="tutorial-ai-panel"
      >
        {/* Header */}
        <div className="tut-ai-header">
          <div className="flex items-center gap-2">
            <div className="tut-ai-header-icon">
              <Sparkles size={16} />
            </div>
            <span className="font-semibold text-sm">Assistant IA</span>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setAIPanelOpen(false)}>
            <X size={14} />
          </Button>
        </div>

        {/* Action Selector */}
        <div className="tut-ai-actions">
          {AI_ACTIONS.map(action => (
            <button
              key={action.id}
              type="button"
              className={`tut-ai-action-btn ${activeAction === action.id ? 'tut-ai-action-active' : ''}`}
              onClick={() => {
                setActiveAction(action.id);
                clearAIResult();
              }}
              title={action.description}
            >
              <action.icon size={14} />
              <span>{action.label}</span>
            </button>
          ))}
        </div>

        {/* Options */}
        <div className="tut-ai-options">
          {activeAction === 'generate' && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Style :</span>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STYLES.map(s => (
                    <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {activeAction === 'code' && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground whitespace-nowrap">Langage :</span>
              <Select value={codeLanguage} onValueChange={setCodeLanguage}>
                <SelectTrigger className="h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CODE_LANGUAGES.map(l => (
                    <SelectItem key={l} value={l} className="text-xs">{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Prompt Input */}
        <form onSubmit={handleSubmit} className="tut-ai-prompt-form">
          <div className="relative">
            <Input
              ref={promptRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={
                activeAction === 'generate'
                  ? 'Décrivez le tutoriel à générer...'
                  : activeAction === 'code'
                  ? 'Décrivez l\'exemple de code...'
                  : 'Collez le texte à traiter ou sélectionnez dans l\'éditeur...'
              }
              className="pr-10 text-sm"
              disabled={aiLoading}
            />
            <Button
              type="submit"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              disabled={aiLoading || !prompt.trim()}
            >
              {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </Button>
          </div>
          {selectedText && ['improve', 'fix', 'simplify', 'formal', 'summarize'].includes(activeAction) && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <Badge variant="secondary" className="text-[10px] py-0">
                <Zap size={10} className="mr-1" />
                Texte sélectionné détecté
              </Badge>
            </div>
          )}
        </form>

        {/* Result Preview */}
        {aiLoading && (
          <div className="tut-ai-loading">
            <Loader2 className="animate-spin" size={20} />
            <span className="text-sm">Génération en cours...</span>
            <span className="text-xs text-muted-foreground">Cela peut prendre quelques secondes</span>
          </div>
        )}

        {aiResult && !aiLoading && (
          <div className="tut-ai-result" ref={resultRef}>
            <div className="tut-ai-result-header">
              <span className="text-xs font-medium text-muted-foreground">Aperçu du résultat</span>
              <Badge variant="outline" className="text-[10px]">
                {aiResult.tokens_used || 0} tokens
              </Badge>
            </div>
            <div className="tut-ai-result-content">
              <div className="tut-ai-result-text">
                {typeof aiResult.raw_text === 'string' ? (
                  aiResult.raw_text.split('\n').map((line, i) => (
                    <p key={i} className={line.startsWith('#') ? 'font-semibold' : ''}>
                      {line || '\u00A0'}
                    </p>
                  ))
                ) : (
                  <p className="text-muted-foreground italic">Contenu structuré (TipTap JSON)</p>
                )}
              </div>
            </div>
            <div className="tut-ai-result-actions">
              <Button size="sm" onClick={handleInsert} className="flex-1">
                <Plus size={14} className="mr-1.5" />
                Insérer
              </Button>
              <Button size="sm" variant="outline" onClick={handleRegenerate}>
                <RefreshCcw size={14} className="mr-1.5" />
                Régénérer
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCopy}>
                <Copy size={14} />
              </Button>
              <Button size="sm" variant="ghost" onClick={clearAIResult}>
                <Eraser size={14} />
              </Button>
            </div>
          </div>
        )}

        {/* History */}
        {aiHistory.length > 0 && (
          <div className="tut-ai-history">
            <button
              type="button"
              className="tut-ai-history-toggle"
              onClick={() => setShowHistory(!showHistory)}
            >
              <MessageSquare size={14} />
              <span className="text-xs font-medium">Historique ({aiHistory.length})</span>
              {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {showHistory && (
              <div className="tut-ai-history-list">
                {aiHistory.slice(0, 10).map((item) => (
                  <div key={item.id} className="tut-ai-history-item">
                    <div className="flex items-center gap-1.5">
                      <Badge variant="secondary" className="text-[10px] py-0">
                        {item.type === 'generate' ? 'Généré' : item.type === 'code' ? 'Code' : item.action || 'IA'}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(item.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {item.prompt || item.topic || '...'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
